import classNames from "classnames";
import * as React from "react";

import {
  Button,
  MultistepDialog,
  DialogStep,
  Classes,
  ButtonProps,
  DialogStepId,
} from "@blueprintjs/core";
import { UserData } from "../types";
import { withToaster } from "../util";
import { createUserAppTourFinished } from "../api";
import { recoverBrowserSecurityError } from "../utils/storage";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import auditFirst from "../assets/AuditFirst.png";
import beConfident from "../assets/BeConfident.png";
import goBroad from "../assets/GoBroad.png";
import writeRules from "../assets/WriteRules.png";
import { PurpleHighlightTagButton } from "../r2components/ThemeButton";

const APP_TOUR_STATE_OPEN = "app_tour_state_open";
const APP_TOUR_STATE_STEP = "app_tour_state_page";

const DialogDiv = styled.div`
  text-align: center;
  min-width: 50em;
`;
const DialogParagraph = styled.p`
  margin-top: 24px;
  margin-bottom: 16px;
  line-height: 24px;
`;
const IconImg = styled.img`
  height: 200px;
`;

const AppTour: React.FC<{
  latestStep: number;
  setLatestStep: (newState: number) => void;
  isTourOpen: boolean;
  setIsTourOpen: (newState: boolean) => void;
}> = ({ latestStep, setLatestStep, isTourOpen, setIsTourOpen }) => {
  const handleClose = () => {
    setIsTourOpen(false);
  };
  const permanentClose = () => {
    withToaster(
      createUserAppTourFinished,
      (err) => `Could not finish the survey on server: ${err.body?.error}`
    )(true).then(handleClose);
  };

  const handleSwitch = (newDialogStepId: DialogStepId, _: DialogStepId) => {
    if (newDialogStepId > latestStep) {
      setLatestStep(Number(newDialogStepId));
    }
  };
  const history = useHistory();
  const [isRedirectingTo, setIsRedirectingTo] = React.useState<string>("");
  React.useEffect(() => {
    // Catching redirects after tour closes due to setters being async...
    if (!isTourOpen && isRedirectingTo) {
      history.push(isRedirectingTo);
    }
  }, [isTourOpen, isRedirectingTo, history]);

  const redirectFurther = (redirectUrl: string) => () => {
    setIsTourOpen(false);
    setIsRedirectingTo(redirectUrl);
  };

  const finalButtonProps: Partial<ButtonProps> = {
    intent: "primary",
    onClick: permanentClose,
    text: "Finish",
  };

  return (
    <MultistepDialog
      className={"appTourDialog"}
      onClose={handleClose}
      nextButtonProps={{ disabled: false }}
      finalButtonProps={finalButtonProps}
      title="Welcome to Semgrep"
      initialStepIndex={latestStep}
      resetOnClose={false}
      isOpen={isTourOpen}
      onChange={handleSwitch}
    >
      <DialogStep
        id={"0"}
        panel={
          <DialogDiv
            className={classNames(Classes.DIALOG_BODY)}
            style={{ textAlign: "center", minWidth: "20em" }}
          >
            <h2> Audit First </h2>
            <br />
            <div>
              <IconImg
                src={auditFirst}
                alt="Alien spaceship adds first Semgrep rule"
              />
            </div>
            <DialogParagraph>
              Start running Semgrep without interfering with the developer{" "}
              <br />
              process. Triage results on the web or push them to Slack.
            </DialogParagraph>
            <Button
              onClick={redirectFurther("/manage/projects")}
              intent="success"
            >
              Add Project
            </Button>
          </DialogDiv>
        }
        title="Audit First"
      />
      <DialogStep
        id={1}
        panel={
          <DialogDiv
            className={classNames(Classes.DIALOG_BODY)}
            style={{ textAlign: "center", minWidth: "20em" }}
          >
            <h2> Be Confident </h2>
            <br />
            <div>
              <IconImg
                src={beConfident}
                alt="Alien spaceship moves a Semgrep rule from audit to blocking"
              />
            </div>
            <DialogParagraph>
              Find results you're confident in? Promote Semgrep findings for{" "}
              <br />
              developers to see at pull and merge request time.
            </DialogParagraph>
            <Button
              onClick={redirectFurther("/manage/projects")}
              intent="success"
            >
              Add Project
            </Button>
          </DialogDiv>
        }
        title="Be Confident"
      />
      <DialogStep
        id={2}
        panel={
          <DialogDiv
            className={classNames(Classes.DIALOG_BODY)}
            style={{ textAlign: "center", minWidth: "20em" }}
          >
            <h2> Go Broad & Deep </h2>
            <br />
            <div>
              <IconImg
                src={goBroad}
                alt="View of outer space with languages and frameworks as planets"
              />
            </div>
            <DialogParagraph>
              Coverage across 17+ languages with 1000+ rules to choose from.{" "}
              <br />
              Explore all our rules in the Registry, then add them to a project.
            </DialogParagraph>
            <Button onClick={redirectFurther("/explore")} intent="success">
              Explore Rules
            </Button>
          </DialogDiv>
        }
        title="Go Broad & Deep"
      />
      <DialogStep
        id={3}
        panel={
          <DialogDiv
            className={classNames(Classes.DIALOG_BODY)}
            style={{ textAlign: "center", minWidth: "20em" }}
          >
            <h2> Write Rules, Today </h2>
            <br />
            <div>
              <IconImg
                src={writeRules}
                alt="An alien writes Semgrep rules in outer space"
              />
            </div>
            <DialogParagraph>
              Everyone has unique coding practices. Codify your security <br />
              (and any other!) conventions with easy to write rules.
            </DialogParagraph>
            <Button onClick={redirectFurther("/playground")} intent="success">
              Write Rules
            </Button>
            <Button
              onClick={redirectFurther("/learn")}
              intent="success"
              outlined={true}
              style={{ margin: "8px" }}
            >
              Visit Tutorial
            </Button>
          </DialogDiv>
        }
        title="Write Rules, Today"
      />
    </MultistepDialog>
  );
};

const AppTourMarker: React.FC<{ user: UserData }> = () => {
  let isTourOpenDefault = React.useMemo(
    () =>
      recoverBrowserSecurityError(() =>
        localStorage.getItem(APP_TOUR_STATE_OPEN)
      ),
    []
  );
  const [isTourOpen, setIsTourOpen] = React.useState<boolean>(
    isTourOpenDefault === null ? true : isTourOpenDefault === "true"
  );
  React.useEffect(() => {
    // Extra braces required as the recover might return null (not void)
    recoverBrowserSecurityError(() =>
      localStorage.setItem(APP_TOUR_STATE_OPEN, String(isTourOpen))
    );
  }, [isTourOpen]);

  let latestStepDefault = React.useMemo(
    () =>
      recoverBrowserSecurityError(() =>
        localStorage.getItem(APP_TOUR_STATE_STEP)
      ),
    []
  );
  const [latestStep, setLatestStep] = React.useState<number>(
    latestStepDefault !== null ? Number(latestStepDefault) : 0
  );
  React.useEffect(() => {
    recoverBrowserSecurityError(() =>
      localStorage.setItem(APP_TOUR_STATE_STEP, String(latestStep))
    );
  }, [latestStep]);

  return (
    <>
      <PurpleHighlightTagButton
        onClick={() => setIsTourOpen(true)}
        text="Tour"
      />
      <AppTour
        latestStep={latestStep}
        setLatestStep={setLatestStep}
        isTourOpen={isTourOpen}
        setIsTourOpen={setIsTourOpen}
      />
    </>
  );
};

export default AppTourMarker;
